import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tipsDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { tipInputSchema } from '../../lib/schemas/tip.js';
import type { Visibility } from '../../types/visibility.js';
import { toAppError } from '../../lib/utils/error.js';
import { Spinner } from '../shared/Spinner.js';
import { MarkdownEditor } from '../shared/MarkdownEditor.js';
import { TagInput } from './TagInput.js';
import { VisibilityRadio } from './VisibilityRadio.js';
import { useDraft } from './useDraft.js';
import { DraftRestoredBanner } from './DraftRestoredBanner.js';
import { ProjectSelectField } from './ProjectSelectField.js';

export interface TipDraftState {
  title: string;
  body: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string | undefined;
}

const INITIAL: TipDraftState = {
  title: '',
  body: '',
  tags: [],
  visibility: 'shared',
};

interface TipFormProps {
  onDone: (id: string) => void;
  editId?: string | undefined;
  initialValues?: TipDraftState | undefined;
}

export function TipForm({ onDone, editId, initialValues }: TipFormProps) {
  const isEditing = !!editId;
  const { fbUser } = useAuth();
  const qc = useQueryClient();
  const draftHook = useDraft<TipDraftState>('tip', INITIAL);
  const [editDraft, setEditDraft] = useState<TipDraftState>(initialValues ?? INITIAL);
  const draft = isEditing ? editDraft : draftHook.value;
  const setDraft = isEditing ? setEditDraft : draftHook.set;
  const clearDraft = isEditing ? () => {} : draftHook.clear;
  const wasRestored = isEditing ? false : draftHook.wasRestored;
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof TipDraftState>(k: K, v: TipDraftState[K]) =>
    setDraft({ ...draft, [k]: v });

  const m = useMutation({
    mutationFn: async () => {
      if (!fbUser) throw new Error('not signed in');
      const parsed = tipInputSchema.parse({
        title: draft.title,
        body: draft.body,
        tags: draft.tags,
        visibility: draft.visibility,
        projectId: draft.projectId,
      });
      const payload = {
        title: parsed.title,
        body: parsed.body,
        tags: parsed.tags,
        visibility: parsed.visibility as Visibility,
        projectId: parsed.projectId,
      };
      if (editId) {
        await tipsDb.update(editId, payload);
        return editId;
      }
      return await tipsDb.create(fbUser.uid, payload);
    },
    onSuccess: async (id) => {
      clearDraft();
      await qc.invalidateQueries({ queryKey: ['tips'] });
      await qc.invalidateQueries({ queryKey: ['projects', 'counts'] });
      await qc.invalidateQueries({ queryKey: ['project'] });
      await qc.invalidateQueries({ queryKey: ['sidebar', 'counts'] });
      if (editId) {
        await qc.refetchQueries({ queryKey: ['tip', editId] });
      }
      onDone(id);
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    m.mutate();
  };

  return (
    <form className="compose-form" onSubmit={onSubmit}>
      {!isEditing && (
        <DraftRestoredBanner visible={wasRestored} onDiscard={clearDraft} />
      )}
      <label className="auth-field">
        <span>タイトル <em className="mono">短く要点を一行で</em></span>
        <input
          required
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
          maxLength={100}
        />
      </label>
      <div className="auth-field">
        <span>本文 <em className="mono">手順・設定・例コードなど（Markdown 可）</em></span>
        <MarkdownEditor
          value={draft.body}
          onChange={(v) => set('body', v)}
          rows={8}
          required
          maxLength={4000}
          ariaLabel="本文"
        />
      </div>
      <div className="auth-field">
        <span>タグ</span>
        <TagInput value={draft.tags} onChange={(t) => set('tags', t)} />
      </div>
      <ProjectSelectField
        value={draft.projectId}
        onChange={(v) => set('projectId', v)}
      />
      <VisibilityRadio value={draft.visibility} onChange={(v) => set('visibility', v)} />
      {error && <div className="auth-error">{error}</div>}
      <div className="compose-foot">
        <span className="mono compose-draft-hint">
          {isEditing ? '編集モード' : '下書きは自動保存されます'}
        </span>
        <button type="submit" className="btn btn-primary" disabled={m.isPending}>
          {m.isPending ? <Spinner /> : isEditing ? '更新' : 'TIPS を保存'}
        </button>
      </div>
    </form>
  );
}
